// 🔧 promotion_content_service.js
// ✅ Purpose: Content Studio service for the LGV Promotion Engine
// ✅ Features:
// Converts Promotion Ideas into Promotion Posts
// New posts begin life in Draft status.
// Saves draft posts into the Promotion Posts collection
// - Preserves traceability back to the original idea
// - Allows body-only Social Post drafts without a title
// - Supports platform, post type, category, campaign, theme, audience, and goal fields
// - Keeps UI pages away from Firestore implementation details
// - Prepares for future OpenAI draft generation
// -// Creates a new Promotion Post.
//
// Every Promotion Post begins in Draft status and
// progresses through its lifecycle using queueStatus
// and publishStatus. Fully block-commented for clarity and future devs

import {
  addDoc,
  doc,
  runTransaction,
  serverTimestamp,
} from "firebase/firestore";
import { db } from "../../firebase";
import { getPromotionPostCapabilities, normalizePromotionPostOptions, normalizePromotionHashtags, buildPromotionPostText } from "../../../functions/shared/promotion_post_capabilities.js";

import {
  getPromotionPostsCollection,
  getPromotionIdeaRef,
} from "./promotion_repository";

import {
  PROMOTION_ENGINE_SOURCES,
  PROMOTION_SCOPES,
} from "../config/promotion_settings_map";

import {
  PROMOTION_IMAGE_STATUS,
  PROMOTION_PUBLISH_STATUS,
  PROMOTION_QUEUE_STATUS,
} from "../constants/promotion_status";

/**
 * createPromotionContentDraft
 *
 * Creates a queue-ready promotional content draft.
 *
 * MVP 1:
 * - Allows LGV admin to manually create content from an idea.
 *
 * Future:
 * - OpenAI will generate the body, hashtags, CTA, and image prompt.
 * - Content Studio will support rewrite, tone, and platform-specific variants.
 */
export const createPromotionContentDraft = async ({
  ideaId = null,
  ideaTitle = "",
  aiUsageKey,
  relatedModule = "",
  relatedId = "",
  relatedName = "",
  relatedScope = "",
  sourceAuctionItemId = "",
  title = "",
  body = "",
  hashtags = [],
  cta = "",
  postOptions = {},
  platform = "",
  platformFeature = "",
  postType = "",
  requestType = "social_post",
  topic = "",
  eventType = "",
  category = "",
  campaign = "",
  theme = "",
  audience = "",
  goal = "",
  imageUrl = "",
  imageStoragePath = "",
  imageUrls = [],
  imageStoragePaths = [],
  rawImages,
  sizedImages = [],
  rawVideo = null,
  videoUrl = "",
  videoStoragePath = "",
  imagePrompt = "",
  aiPrompt = "",
  scheduledAt = null,
  createdBy = "",
  scope = PROMOTION_SCOPES.INTERNAL_LGV,
  eventId = null,
  organizationId = null,
  engineSource = PROMOTION_ENGINE_SOURCES.MANUAL,
}) => {
  /**
   * Guardrails keep incomplete queue items
   * from being created.
   */
  if (requestType !== "social_post" && (!title || !title.trim())) {
    throw new Error("Content title is required.");
  }

  if (!body || !body.trim()) {
    throw new Error("Post body is required.");
  }

  if (!platform) {
    throw new Error("Platform is required.");
  }

  if (!postType) {
    throw new Error("Post type is required.");
  }
  if (requestType === "social_post" && (typeof aiPrompt !== "string" || !aiPrompt.trim())) {
    throw new Error("AI Prompt / Idea is required.");
  }
  const capabilities = getPromotionPostCapabilities(platform, postType);
  const normalizedOptions = requestType === "social_post" ? normalizePromotionPostOptions(platform, postType, postOptions, {
    imageCount: (rawImages || imageUrls || []).length, videoDurationSeconds: rawVideo?.durationSeconds,
  }) : {};
  const normalizedHashtags = requestType === "social_post" ? normalizePromotionHashtags(platform, postType, hashtags) : hashtags;
  const captionCta = requestType !== "social_post" || capabilities.supportsCaption ? String(cta || "").trim() : "";
  if (requestType === "social_post" && capabilities.supportsCaption) buildPromotionPostText({ title, body, cta: captionCta, hashtags: normalizedHashtags, platform, postType });

  const postPayload = {
    /**
     * Traceability
     */
    ideaId,
    ideaTitle,
    ...(aiUsageKey ? { aiUsageKey } : {}),
    relatedModule,
    relatedId,
    relatedName,
    ...(relatedScope ? { relatedScope } : {}),
    sourceAuctionItemId,

    /**
     * Content
     */
    title: title.trim(),
    body: body.trim(),
    hashtags: normalizedHashtags,
    cta: captionCta,
    postOptions: normalizedOptions,

    /**
     * Classification
     */
    platform,
    platformFeature,
    postType,
    requestType,
    topic,
    eventType,
    category,
    campaign,
    theme,
    audience,
    goal,

    /**
     * Media
     */
    imageUrl,
    imageStoragePath,
    imageUrls,
    imageStoragePaths,
    ...(Array.isArray(rawImages) ? { rawImages } : {}),
    sizedImages,
    rawVideo,
    videoUrl,
    videoStoragePath,
    imagePrompt,
    aiPrompt: aiPrompt.trim(),

    /**
     * Scheduling
     */
    scheduledAt,

    /**
     * MVP 2 compatibility
     */
    scope,
    eventId,
    organizationId,
    engineSource,

    /**
     * Workflow state machines
     */
    queueStatus: PROMOTION_QUEUE_STATUS.DRAFT,
    imageStatus: imageUrl
      ? PROMOTION_IMAGE_STATUS.SELECTED
      : PROMOTION_IMAGE_STATUS.NONE,
    publishStatus: PROMOTION_PUBLISH_STATUS.NOT_READY,

    /**
     * Metadata
     */
    aiGenerated: false,
    createdBy,
    updatedBy: createdBy,
    createdAt: serverTimestamp(),
    updatedAt: serverTimestamp(),
  };

  // Pin the Idea prompt and create its variation together. Concurrent first
  // saves cannot give one Idea two different prompts or leave an orphan post.
  let docRef;
  if (ideaId && requestType === "social_post") {
    docRef = doc(getPromotionPostsCollection());
    await runTransaction(db, async (transaction) => {
      const ideaRef = getPromotionIdeaRef(ideaId);
      const snapshot = await transaction.get(ideaRef);
      if (!snapshot.exists()) throw new Error("Promotion idea not found.");
      const idea = snapshot.data();
      if ((idea.eventId || null) !== (eventId || null)) throw new Error("This idea belongs to another event.");
      const ideaPrompt = typeof idea.aiPrompt === "string" ? idea.aiPrompt.trim() : "";
      if (ideaPrompt && ideaPrompt !== postPayload.aiPrompt) {
        throw new Error("This Idea already has a saved AI Prompt / Idea. Open the Idea again to create another variation.");
      }
      if (!ideaPrompt) {
        transaction.update(ideaRef, { aiPrompt: postPayload.aiPrompt, updatedBy: createdBy, updatedAt: serverTimestamp() });
      }
      transaction.set(docRef, postPayload);
    });
  } else {
    docRef = await addDoc(getPromotionPostsCollection(), postPayload);
  }

  return {
    id: docRef.id,
    ...postPayload,
  };

};
